import { DATA_KEYS, writeJSON, readJSON } from "@/storage/local";
import { scopedKey } from "@/storage/session";

// Escribe en la cuenta abierta los datos leídos de un informe.
//
// Es la única operación de la app que sustituye datos que el usuario no acaba
// de escribir, así que antes de nada se guarda lo que hubiera. Si la
// restauración sale mal —o restaura menos de lo que había—, se puede volver.

const RESPALDO = "tryad_previo_";

// Copia de lo que hay ahora, en claves propias que no se sincronizan ni se
// borran solas. Se sobrescribe cada vez: interesa el estado justo anterior a la
// última restauración, no un archivo histórico.
function respaldar() {
  const guardadas = [];
  Object.values(DATA_KEYS).forEach((baseKey) => {
    const actual = localStorage.getItem(scopedKey(baseKey));
    if (actual === null) return;
    try {
      localStorage.setItem(scopedKey(`${RESPALDO}${baseKey}`), actual);
      guardadas.push(baseKey);
    } catch {
      // Sin espacio para el respaldo se sigue: el usuario ya ha confirmado que
      // quiere restaurar, y negárselo por no poder guardar la copia sería peor.
    }
  });
  return guardadas;
}

export function hasBackup() {
  return Object.values(DATA_KEYS).some(
    (baseKey) => localStorage.getItem(scopedKey(`${RESPALDO}${baseKey}`)) !== null
  );
}

// Deshacer la última restauración.
export function restoreBackup() {
  let devueltas = 0;
  Object.values(DATA_KEYS).forEach((baseKey) => {
    const copia = localStorage.getItem(scopedKey(`${RESPALDO}${baseKey}`));
    if (copia === null) return;
    localStorage.setItem(scopedKey(baseKey), copia);
    // Pasa por writeJSON para que quede sellada y programe su subida; si no, la
    // nube volvería a imponer lo restaurado en la siguiente recarga.
    try {
      writeJSON(baseKey, JSON.parse(copia));
      devueltas++;
    } catch {
      /* una copia ilegible se queda como está */
    }
  });
  return devueltas;
}

// Las imágenes no viajan en el informe: son GIFs de un CDN o data URIs que lo
// engordarían hasta lo impracticable. Pero la cuenta que se está restaurando
// suele conservarlas, porque lo que se perdió fue el progreso y no la rutina.
//
// Así que se rescatan de la rutina que haya ahora, emparejando por nombre de
// ejercicio, que es lo único estable. Sin esto, restaurar el progreso costaría
// las fotos que el usuario había puesto una a una.
function conservarImagenes(rutinaNueva) {
  const actual = readJSON(DATA_KEYS.APP, null);
  if (!actual || !Array.isArray(actual.routine)) return { routine: rutinaNueva, recuperadas: 0 };

  const porNombre = new Map();
  actual.routine.forEach((dia) => {
    (dia.exercises || []).forEach((ejercicio) => {
      if (!porNombre.has(ejercicio.name)) porNombre.set(ejercicio.name, ejercicio);
    });
  });

  let recuperadas = 0;
  const routine = rutinaNueva.map((dia) => ({
    ...dia,
    exercises: dia.exercises.map((ejercicio) => {
      const anterior = porNombre.get(ejercicio.name);
      if (!anterior) return ejercicio;
      const conservado = { ...ejercicio };
      if (anterior.img) {
        conservado.img = anterior.img;
        recuperadas++;
      }
      if (anterior.libraryId) conservado.libraryId = anterior.libraryId;
      if (Array.isArray(anterior.variants) && anterior.variants.length > 0) {
        conservado.variants = anterior.variants;
      }
      return conservado;
    }),
  }));

  return { routine, recuperadas };
}

export function applyImport(datos) {
  const respaldadas = respaldar();
  const { routine, recuperadas } = conservarImagenes(datos.routine);

  writeJSON(DATA_KEYS.APP, {
    routine,
    progress: datos.progress,
    alternatives: {},
    userName: datos.userName,
    week: datos.week,
    weekStartedAt: null,
  });

  writeJSON(DATA_KEYS.WEEKS, datos.weeks);
  writeJSON(DATA_KEYS.HISTORY, datos.history);

  // Los días entrenados se suman, no se sustituyen. Son un registro acumulativo
  // y quitar uno no arregla nada; además, un informe reconstruido a partir de un
  // PDF no los trae —ese formato solo dice cuántos hubo cada mes—, y sin esto
  // restaurar desde un PDF borraría la racha entera.
  const diasPrevios = readJSON(DATA_KEYS.TRAINING_DATES, []);
  const dias = [...new Set([...(Array.isArray(diasPrevios) ? diasPrevios : []), ...datos.trainingDates])].sort();
  writeJSON(DATA_KEYS.TRAINING_DATES, dias);

  if (Object.keys(datos.measurements).length > 0) {
    writeJSON(DATA_KEYS.MEASUREMENTS, datos.measurements);
  }
  if (datos.customMetrics.length > 0) {
    writeJSON(DATA_KEYS.CUSTOM_METRICS, datos.customMetrics);
  }
  if (datos.weeklyGoal) {
    writeJSON(DATA_KEYS.WEEKLY_GOAL, datos.weeklyGoal);
  }
  if (datos.diet) {
    writeJSON(DATA_KEYS.DIET, datos.diet);
  }

  return { respaldadas, imagenesConservadas: recuperadas };
}
